import { useCallback, useContext, useEffect, useState } from "react";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { getAnswerColor } from "@/components/QuestionnaireReport";
import { Badge } from "@/components/reports/Badges";
import DiagramModal from "@/components/reports/DiagramModal";
import {
  SnpAccordionRow,
  FunctionalMarkerRow,
  PotentialSymptomsRow
} from "@/components/reports/InterpretationSharedRows";
import MarkersAssessedChart from "@/components/reports/MarkersAssessedChart";
import CardListContext from "@/contexts/CardListContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  PATIENT_NOTIFICATION_ALL,
  PATIENT_NOTIFICATION_EXCLUDE_RESULTS,
  PATIENT_NOTIFICATION_NONE,
  REPORT_PUBLICATION_STATUS_APPROVED,
  REPORT_PUBLICATION_STATUS_FOR_REVIEW
} from "@/core/constants";
import { CMP_BUSINESS_TAB_URL, CMP_PATIENTS_REPORT_PRINT_URL, getCmpUrl } from "@/core/urls";
import Accordion from "@/tpo/Accordion";
import Checkbox from "@/tpo/Checkbox";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import LinkWrapper from "@/tpo/LinkWrapper";
import Modal from "@/tpo/Modal";
import Stack from "@/tpo/Stack";
import { ViewerContext } from "@/tpo/Viewer";
import { mapDnaDatapointToSnpRow } from "@/utils/getSnpColor";
import { getInterpretationColor } from "@/utils/shared";
import ButtonV2 from "@/v2/Buttons";
import { useMutation } from "@apollo/client";
import CrossIcon from "@/images/cross.svg?react";
import TickIcon from "@/images/tick.svg?react";
import ReportInformationPanel from "@/components/reports/ReportInformationPanel";
import { RELEASE_USER_REPORT_MUTATION } from "../../graphql/queries";
import RelatedTestsSection from "./RelatedTestsSection";


function ReleaseStatusPanel({
  userReport,
  canShowAutoReleaseModal,
  canShowUnavailableReleaseModal,
  openReleaseModal,
  printReportUrl
}) {
  const publicationStatus = userReport?.publicationStatus;
  const publishedViews = userReport?.publishedViews || [];
  // For now, release completeness is based on the sections currently selectable in the modal.
  const allShareableViews = ["overview", "interpretation"];
  const hasReleasedAllViews = allShareableViews.every(view => publishedViews.includes(view));
  const isApproved = publicationStatus == REPORT_PUBLICATION_STATUS_APPROVED;
  const isUnreleased = !userReport || publicationStatus == REPORT_PUBLICATION_STATUS_FOR_REVIEW;
  const releaseBadgeLabel = isApproved
    ? hasReleasedAllViews
      ? "released"
      : "partial release"
    : "unreleased";
  const releaseBadgeBg = isApproved ? (hasReleasedAllViews ? "green" : "dark") : "red";
  const approvedSectionStatuses = [
    { label: "Overview", released: publishedViews.includes("overview") },
    { label: "Interpretation", released: publishedViews.includes("interpretation") }
  ];

  return (
    <Stack
      bg="white"
      borderRadius={20}
      px={[20, 20, 40]}
      py={40}
      maxWidth={640}
      mx="auto"
      gap={[24, 24, 40]}
    >
      <Stack gap={16}>
        <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]} lineHeight={["24px", "24px", "36px"]}>
          Patient Report status
        </Box>
        <Box
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight={["24px", "24px", "28px"]}
        >
          {isUnreleased
            ? "This report can be made available to your patient. You can manage what is shared with them below."
            : "This report has been released. You can update what is shared with your patient below."}
        </Box>
      </Stack>

      {isApproved && (
        <Stack gap={16} flexWrap="wrap">
          <Group gap={8} alignItems="center" flexWrap="wrap">
            <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              Release status:
            </Box>
            <Badge bg={releaseBadgeBg} color="white">
              {releaseBadgeLabel}
            </Badge>
          </Group>
          <Group gap={24} flexWrap="wrap">
            {approvedSectionStatuses.map(({ label, released }) => (
              <Group
                key={label}
                gap={10}
                alignItems="center"
                bg="iceBlue"
                borderRadius={24}
                py="8px"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                {released ? <TickIcon fill="#00BF86" width={24} /> : <CrossIcon fill="#EA4A4A" width={18} />}
                </Box>
                <Box fontFamily="gilroyMedium" fontSize={[13, 13, 14]}>
                  {label}
                </Box>
              </Group>
            ))}
          </Group>
        </Stack>
      )}

      <Group flexDirection={["column", "column", "row"]} gap={12} alignItems="flex-start">
        {isUnreleased ? (
          <ButtonV2
            color="green"
            withChevron
            onClick={() => {
              if (canShowAutoReleaseModal) {
                openReleaseModal({ isUpdate: false });
                return;
              }
              if (canShowUnavailableReleaseModal) {
                openReleaseModal({ isUnavailable: true });
              }
            }}
          >
            release report
          </ButtonV2>
        ) : (
          canShowAutoReleaseModal && (
            <ButtonV2 color="dark" withChevron onClick={() => openReleaseModal({ isUpdate: true })}>
              update
            </ButtonV2>
          )
        )}
        {printReportUrl && (
          <ButtonV2
            color="dark"
            as={LinkWrapper}
            to={printReportUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline={false}
          >
            print report
          </ButtonV2>
        )}
      </Group>
    </Stack>
  );
}

export default function UnlockedOverviewTabContent({ report, userReport }) {
  const { organisation, organisationId } = useOrganisationContext();
  const { userId } = useContext(ViewerContext);
  const [activeDiagram, setActiveDiagram] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [showUnavailableReleaseModal, setShowUnavailableReleaseModal] = useState(false);
  const [releaseToPatientSuccess, setReleaseToPatientSuccess] = useState(false);
  const [isUpdatingRelease, setIsUpdatingRelease] = useState(false);
  const [releaseError, setReleaseError] = useState();
  const [sectionsToShareError, setSectionsToShareError] = useState();
  const [sectionsToShare, setSectionsToShare] = useState({
    overview: false,
    summary: false,
    interpretations: false,
    suggestions: false
  });
  const [releaseUserReportMutation, { loading: isReleasing }] = useMutation(
    RELEASE_USER_REPORT_MUTATION
  );

  useEffect(() => {
    const publishedViews = userReport?.publishedViews || [];
    setSectionsToShare({
      overview: publishedViews.includes("overview"),
      summary: publishedViews.includes("summary"),
      interpretations: publishedViews.includes("interpretation"),
      suggestions: publishedViews.includes("suggestions")
    });
  }, [userReport?.publishedViews]);

  const handleToggleSection =
    sectionKey =>
    ({ target: { checked } }) => {
      setSectionsToShare(prev => ({
        ...prev,
        [sectionKey]: checked
      }));
      if (checked) {
        setSectionsToShareError(undefined);
      }
    };

  const hasSelectedSection = Object.values(sectionsToShare).some(Boolean);

  const releaseReport = useCallback(
    ({ showSuccessScreen = false } = {}) => {
      if (!organisationId || !userId || !report?.slug) return;
      setReleaseError(undefined);
      releaseUserReportMutation({
        variables: {
          organisationId,
          userId,
          reportSlug: report.slug,
          overview: sectionsToShare.overview,
          summary: sectionsToShare.summary,
          interpretation: sectionsToShare.interpretations,
          suggestions: sectionsToShare.suggestions
        }
      })
        .then(response => {
          if (response?.data?.releaseUserReportMutation?.ok) {
            if (showSuccessScreen) {
              setReleaseToPatientSuccess(true);
              return;
            }
            setShowReleaseModal(false);
            return;
          }
          setReleaseError(
            "Failed to release the user's report. Please contact info@regeneruslabs.com if this issue persists."
          );
        })
        .catch(() => {
          setReleaseError(
            "Failed to release the user's report. Please contact info@regeneruslabs.com if this issue persists."
          );
        });
    },
    [organisationId, report?.slug, releaseUserReportMutation, sectionsToShare, userId]
  );

  const releaseReportToPatient = useCallback(() => {
    if (!hasSelectedSection) {
      setSectionsToShareError("Please choose at least one section to share before releasing.");
      return;
    }

    setSectionsToShareError(undefined);
    releaseReport({ showSuccessScreen: true });
  }, [hasSelectedSection, releaseReport]);

  const tabReleaseStatuses = [
    { tab: "Overview", released: sectionsToShare.overview },
    // { tab: "Summary", released: sectionsToShare.summary },
    { tab: "Interpretation", released: sectionsToShare.interpretations },
    // { tab: "Suggestions", released: sectionsToShare.suggestions }
  ];

  const snpRows = (userReport?.dnaDatapointsOutOfRange || []).map(mapDnaDatapointToSnpRow);

  const snpsForDiagram = userReport?.snpsForDiagram || {};
  const expectedSnps = userReport?.expectedSnps || {};

  const ctx = useContext(CardListContext);

  const functionalRows = (userReport?.functionalDatapointsOutOfRange || []).map(datapoint => ({
    title: datapoint?.datapoint?.name,
    sampleType: datapoint?.sampleType,
    dateExtracted: datapoint?.dateExtracted,
    accentColor: getInterpretationColor(datapoint?.chartConfiguration?.interpretation),
    badgeText: datapoint?.value,
    units: datapoint?.datapoint?.units,
    onClick: () => {
      ctx?.viewCard({
        type: "datapoints",
        item: datapoint
      });
    }
  }));

  const symptomRows = [...(userReport?.questionsAndAnswersOutOfRange || [])]
    ?.sort((a, b) => b.answerScore - a.answerScore)
    .map(q => ({
      title: q.question.symptomGroup,
      risk: q.answerText || "No Answer",
      color: q.answerScore != null ? getAnswerColor(q.answerScore) : "midGrey",
      key: `symptom-${q.id}`
    }));

  const patientNotificationSetting =
    organisation?.patientNotifications == PATIENT_NOTIFICATION_ALL
      ? {
          label: "opt in all",
          helpText:
            "Your patient will get email alerts for new test orders and when results are available."
        }
      : null;
  const unavailableReleaseNotificationSetting =
    organisation?.patientNotifications == PATIENT_NOTIFICATION_EXCLUDE_RESULTS
      ? {
          label: "opt out results",
          helpText:
            "Your patient will receive notifications only for new orders and will not be contacted when results are available."
        }
      : organisation?.patientNotifications == PATIENT_NOTIFICATION_NONE
      ? {
          label: "opt out all",
          helpText: "No email communications will be sent to your patients"
        }
      : null;

  const settingsUrl = organisation?.hashId
    ? getCmpUrl(CMP_BUSINESS_TAB_URL, {
        organisationId: organisation.hashId,
        tab: "settings"
      })
    : undefined;
  const printReportUrl =
    organisation?.hashId && report?.slug && userId
      ? getCmpUrl(CMP_PATIENTS_REPORT_PRINT_URL, {
          organisationId: organisation.hashId,
          patientId: userId,
          reportSlug: report.slug
        })
      : undefined;
  const canShowAutoReleaseModal = organisation?.patientNotifications == PATIENT_NOTIFICATION_ALL;
  const canShowUnavailableReleaseModal = Boolean(unavailableReleaseNotificationSetting);
  const openReleaseModal = ({ isUpdate = false, isUnavailable = false } = {}) => {
    if (isUnavailable) {
      setShowUnavailableReleaseModal(true);
      return;
    }
    setShowUnavailableReleaseModal(false);
    setReleaseToPatientSuccess(false);
    setReleaseError(undefined);
    setSectionsToShareError(undefined);
    setIsUpdatingRelease(isUpdate);
    setShowReleaseModal(true);
  };

  return (
    <Stack gap={40}>
      <ReleaseStatusPanel
        userReport={userReport}
        canShowAutoReleaseModal={canShowAutoReleaseModal}
        canShowUnavailableReleaseModal={canShowUnavailableReleaseModal}
        openReleaseModal={openReleaseModal}
        printReportUrl={printReportUrl}
      />
      {report?.reportInformationPanels?.map(panel => (
        <ReportInformationPanel
          key={panel.id}
          {...panel}
          diagram={panel.diagram}
          onClick={() => setActiveDiagram({ diagramName: panel.diagram, title: panel.title })}
        />
      ))}
      <MarkersAssessedChart
        snpCounts={{
          inRange: userReport?.dnaDatapointCounts?.inRangeCount || 0,
          outOfRange: userReport?.dnaDatapointCounts?.outOfRangeCount || 0,
          untested: userReport?.dnaDatapointCounts?.untestedCount || 0
        }}
        functionalCounts={{
          inRange: userReport?.functionalDatapointCounts?.inRangeCount || 0,
          outOfRange: userReport?.functionalDatapointCounts?.outOfRangeCount || 0,
          untested: userReport?.functionalDatapointCounts?.untestedCount || 0
        }}
        symptomCounts={{
          inRange: userReport?.questionsAndAnswersCounts?.inRangeCount || 0,
          outOfRange: userReport?.questionsAndAnswersCounts?.outOfRangeCount || 0,
          untested: userReport?.questionsAndAnswersCounts?.untestedCount || 0
        }}
        sx={{ maxWidth: 720, width: "100%", mx: "auto" }}
      />
      {userReport?.dnaDatapointsOutOfRange?.length ||
      userReport?.functionalDatapointsOutOfRange?.length ||
      userReport?.questionsAndAnswersOutOfRange?.length ? (
        <Stack
          gap={[28, 28, 48]}
          bg="white"
          borderRadius={20}
          px={[24, 24, 40]}
          pt={[40, 40, 60]}
          pb={[60, 60, 80]}
          maxWidth={1152}
          mx="auto"
          width="100%"
        >
          <Stack gap={16}>
            <Box
              fontFamily="gilroyBold"
              fontSize={[32, 32, 44]}
              lineHeight={["36px", "36px", "50px"]}
            >
              Out-of-range markers
            </Box>
            <Box
              fontFamily="gilroyMedium"
              fontSize={[14, 14, 16]}
              lineHeight={["24px", "24px", "28px"]}
            >
              The following results are outside of the normal range and could indicate areas of
              focus. Visit the Interpretation tab to see all markers related to {report?.name}.
            </Box>
          </Stack>
          <Stack gap={16}>
            <Box
              fontFamily="gilroyBold"
              fontSize={[16, 16, 18]}
              lineHeight={["24px", "24px", "28px"]}
            >
              SNPS
            </Box>
            <Stack gap={0}>
              <Accordion size={0} variant="default">
                {snpRows.map((row, idx) => (
                  <SnpAccordionRow
                    key={`summary-snp-${row.badgeText}-${idx}`}
                    row={row}
                    isLast={idx === snpRows.length - 1}
                  />
                ))}
              </Accordion>
            </Stack>
          </Stack>
          <Stack gap={16}>
            <Box fontFamily="gilroyBold" fontSize={[18, 18, 20]} lineHeight="1.2">
              Functional markers
            </Box>
            <Stack gap={0}>
              {functionalRows.map((row, idx) => (
                <Box
                  key={`summary-functional-${idx}`}
                  sx={
                    idx === functionalRows.length - 1 ? { "& > div": { borderBottom: "none" } } : {}
                  }
                >
                  <FunctionalMarkerRow
                    title={row.title}
                    sampleType={row.sampleType}
                    dateExtracted={row.dateExtracted}
                    accentColor={row.accentColor}
                    badgeText={row.badgeText}
                    units={row.units}
                    onClick={row.onClick}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
          <Stack gap={0}>
            <Stack gap={8}>
              <Box fontFamily="gilroyBold" fontSize={[18, 18, 20]} lineHeight="1.2">
                Experienced Symptoms
              </Box>
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="28px" mt={"-8px"}>
                The following symptoms have been reported in the report symptomology questionnaire
              </Box>
            </Stack>
            <Box pt={16} />
            {symptomRows?.map(row => (
              <PotentialSymptomsRow
                title={row.title}
                risk={row.risk}
                color={row.color}
                key={row.key}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}
      <RelatedTestsSection report={report} />
      <Modal
        mode={["fullScreen", "fullScreen", "centered"]}
        closeButton
        show={canShowAutoReleaseModal && showReleaseModal}
        close={() => {
          setShowReleaseModal(false);
          setReleaseToPatientSuccess(false);
          setReleaseError(undefined);
          setSectionsToShareError(undefined);
          setIsUpdatingRelease(false);
        }}
        maxWidth={[1024]}
        bg="white"
        headerProps={{
          p: [20, 20, 40]
        }}
        borderRadius={20}
      >
        <Stack>
          <Stack gap={40} maxWith={720} mx="auto" pb={[80]}>
            {releaseToPatientSuccess ? (
              <Stack gap={24}>
                <Stack gap={16}>
                  <Box fontFamily="gilroyBold" fontSize={[24, 28, 32]}>
                    {isUpdatingRelease ? "Report sharing updated" : "Report released to patient"}
                  </Box>
                  <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="26px">
                    {isUpdatingRelease
                      ? "Your patient's report sharing settings have been updated."
                      : "Your patient's report is now live in their account and an email notification will be sent shortly."}
                  </Box>
                </Stack>
                <Group gap={24} flexWrap="wrap">
                  {tabReleaseStatuses.map(({ tab, released }) => (
                    <Group
                      key={tab}
                      gap={8}
                      alignItems="center"
                      bg="iceBlue"
                      borderRadius={24}
                      py="10px"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {released ? (
                          <TickIcon fill="#00BF86" width={24} />
                        ) : (
                          <CrossIcon fill="#EA4A4A" width={18} />
                        )}
                      </Box>
                      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
                        {tab}
                      </Box>
                    </Group>
                  ))}
                </Group>
              </Stack>
            ) : (
              <>
                <Stack gap={16}>
                  <Box fontFamily="gilroyBold" fontSize={[24, 28, 32]}>
                    {isUpdatingRelease ? "Update report sharing" : "Release report to patient"}
                  </Box>
                  <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="26px">
                    After reviewing your client&apos;s report, you can make it available in their
                    patient account.
                  </Box>
                </Stack>
                <Stack gap={12}>
                  <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                    Choose which sections to share:
                  </Box>
                  <Stack gap={10}>
                    <Checkbox
                      label="Overview"
                      checked={sectionsToShare.overview}
                      onChange={handleToggleSection("overview")}
                    />
                    <Checkbox
                      label="Interpretations"
                      checked={sectionsToShare.interpretations}
                      onChange={handleToggleSection("interpretations")}
                    />
                  </Stack>
                </Stack>
                <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="26px">
                  Once released, your patient will receive an email letting them know their report
                  is ready to view.
                </Box>
                {!!patientNotificationSetting && !!settingsUrl && (
                  <Stack gap={16}>
                    <Box>
                      <Group gap={8}>
                        <Box as="span" fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                          Patient notification setting:
                        </Box>
                        <Badge size={["xs"]} color="white" bg="dark">
                          {patientNotificationSetting.label}
                        </Badge>
                      </Group>
                      <Box fontFamily="gilroyMedium" fontSize={14}>
                        {patientNotificationSetting.helpText}
                      </Box>
                    </Box>
                    <ButtonV2
                      size="xs"
                      withChevron
                      color="dark"
                      sx={{
                        fontSize: 10,
                        lineSpacing: "1.5px",
                        mr: "auto"
                      }}
                      as={LinkWrapper}
                      to={settingsUrl}
                      underline={false}
                    >
                      update settings
                    </ButtonV2>
                  </Stack>
                )}
                <Divider />
                <ButtonV2
                  color="green"
                  withChevron
                  sx={{
                    mr: "auto"
                  }}
                  onClick={releaseReportToPatient}
                  disabled={isReleasing}
                >
                  {isUpdatingRelease ? "update sharing" : "release to patient"}
                </ButtonV2>
                {!!sectionsToShareError && <Error error={sectionsToShareError} />}
                {!!releaseError && <Error error={releaseError} />}
              </>
            )}
          </Stack>
        </Stack>
      </Modal>
      <Modal
        mode={["fullScreen", "fullScreen", "centered"]}
        closeButton
        show={showUnavailableReleaseModal}
        close={() => {
          setShowUnavailableReleaseModal(false);
        }}
        maxWidth={[1024]}
        bg="white"
        headerProps={{
          p: [20, 20, 40]
        }}
        borderRadius={20}
      >
        <Stack>
          <Stack gap={40} maxWith={720} mx="auto" pb={[80]}>
            <Stack gap={16}>
              <Box fontFamily="gilroyBold" fontSize={[24, 28, 32]}>
                Release report unavailable
              </Box>
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="26px">
                With your current notification settings, this report <b>will not</b> appear on the
                patient platform and <b>no</b> communications will be sent.
              </Box>
            </Stack>
            {!!unavailableReleaseNotificationSetting && (
              <Stack gap={16}>
                <Box>
                  <Group gap={8}>
                    <Box as="span" fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                      Patient notification setting:
                    </Box>
                    <Badge size={["xs"]} color="white" bg="dark">
                      {unavailableReleaseNotificationSetting.label}
                    </Badge>
                  </Group>
                  <Box fontFamily="gilroyMedium" fontSize={14}>
                    {unavailableReleaseNotificationSetting.helpText}
                  </Box>
                </Box>
                {!!settingsUrl && (
                  <ButtonV2
                    size="xs"
                    withChevron
                    color="dark"
                    sx={{
                      fontSize: 10,
                      lineSpacing: "1.5px",
                      mr: "auto"
                    }}
                    as={LinkWrapper}
                    to={settingsUrl}
                    underline={false}
                  >
                    update settings
                  </ButtonV2>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Modal>
      <DiagramModal
        show={Boolean(activeDiagram)}
        close={() => setActiveDiagram(null)}
        diagramName={activeDiagram?.diagramName}
        title={activeDiagram?.title}
        snpsForDiagram={snpsForDiagram}
        expectedSnps={expectedSnps}
      />
    </Stack>
  );
}
